import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Deliberately shorter than the 150ms drop settle (`board-drop-animation.ts`): this runs while the
 * player is still dragging, and a swap they can change their mind about has to resolve inside the
 * gesture. Long enough to read as movement — which is what keeps a swap on the cusp from reading as a
 * flicker — and short enough that re-crossing the line immediately is a second visible slide rather
 * than a smear of half-finished ones.
 */
const SLIDE_DURATION_MS = 80;

/** Below a pixel of travel there is nothing to see, and sub-pixel reflow would animate constantly. */
const MIN_SLIDE_PX = 1;

export interface TileSlide {
  /** The ref to give the element tracked under `id`. Stable per id, so React never re-attaches it. */
  ref: (id: string) => (element: HTMLElement | null) => void;
}

/**
 * Slides tracked elements from wherever they were to wherever this render has just put them — a FLIP,
 * so that tiles shuffling aside to open a gap read as making room rather than blinking into their new
 * places. The elements are laid out entirely by their container; nothing here moves them, it only
 * animates the move away.
 *
 * Put the ref on an element *inside* the drop target, never on the target itself: dnd-kit re-measures
 * droppables as the DOM changes, and a rect caught mid-slide would report the animating position.
 */
export const useTileSlide = (enabled: boolean): TileSlide => {
  const elements = useRef(new Map<string, HTMLElement>());
  const refs = useRef(new Map<string, (element: HTMLElement | null) => void>());
  const lefts = useRef(new Map<string, number>());
  const running = useRef(new Map<string, Animation>());

  const ref = useCallback((id: string) => {
    const existing = refs.current.get(id);
    if (existing) return existing;

    const callback = (element: HTMLElement | null) => {
      if (element) {
        elements.current.set(id, element);
        return;
      }
      elements.current.delete(id);
      lefts.current.delete(id);
      running.current.delete(id);
      refs.current.delete(id);
    };
    refs.current.set(id, callback);
    return callback;
  }, []);

  // No dependency list: every commit is a chance for the container to have moved something, and the
  // measurement has to happen before the browser paints the new layout.
  useLayoutEffect(() => {
    const previous = lefts.current;
    const next = new Map<string, number>();

    for (const [id, element] of elements.current) {
      // `offsetLeft` is layout only — a slide in flight does not move it — so it says whether the
      // container has actually put the element somewhere new, however many commits a drag causes.
      const layout = element.offsetLeft;
      next.set(id, layout);

      const animation = running.current.get(id);
      // Still headed for the same place: let the slide finish. Restarting it on every unrelated commit
      // (a drag re-renders on each pointer move) would re-run the easing from the top, and the tile
      // would stutter rather than glide.
      if (animation && previous.get(id) === layout) continue;

      // A slide still in flight draws the element away from where layout has it, and that drawn
      // position is where the next slide must start from — otherwise re-aiming mid-shuffle snaps the
      // tile back to its slot first. Read it before cancelling, which is what restores the true rect.
      const drawn = animation ? element.getBoundingClientRect().left : null;
      animation?.cancel();
      running.current.delete(id);

      const before = previous.get(id);
      if (before == null && drawn == null) continue;
      const shift = drawn != null ? drawn - element.getBoundingClientRect().left : (before ?? layout) - layout;
      // `animate` is absent in some test environments; a tile that cannot slide simply appears in place.
      if (!enabled || Math.abs(shift) < MIN_SLIDE_PX || typeof element.animate !== 'function') continue;

      const slide = element.animate([{ transform: `translateX(${shift}px)` }, { transform: 'translateX(0)' }], {
        duration: SLIDE_DURATION_MS,
        easing: 'ease-out',
      });
      slide.onfinish = () => running.current.delete(id);
      running.current.set(id, slide);
    }

    lefts.current = next;
  });

  return { ref };
};
